import { z } from "zod";
import i18n from "@/i18n";

const t = (key: string) => i18n.t(key, { ns: "validation" });

// Schema para cadastro de vacina
export const vaccineSchema = z.object({
  name: z.string().min(1, t("nameRequired")).max(200, t("nameTooLong")),
  manufacturer: z.string().optional().or(z.literal("")),
  species_id: z.string().uuid().optional().or(z.literal("")),
  dose_ml: z.number().positive(t("positiveInteger")).optional(),
  min_age_days: z.number().int().nonnegative(t("minAgeNonNegative")).optional(),
  interval_days: z.number().int().positive(t("positiveInteger")).optional(),
  total_doses: z.number().int().positive(t("positiveInteger")).default(1),
  description: z.string().optional().or(z.literal("")),
  is_active: z.boolean().optional().default(true),
});

// Schema para registro de vacinação
export const petVaccinationSchema = z.object({
  pet_id: z.string().uuid(t("selectPatient")),
  item_id: z.preprocess(
    (value) => (value === "" ? null : value),
    z.string().uuid().optional().nullable(),
  ),
  next_application_item_id: z.preprocess(
    (value) => (value === "" ? null : value),
    z.string().uuid().optional().nullable(),
  ),
  appointment_id: z.string().uuid().optional().nullable().or(z.literal("")),
  medical_record_id: z.string().uuid().optional().nullable(),
  clinic_id: z.string().uuid(t("clinicRequired")),
  application_date: z.date({ required_error: t("applicationDateRequired") }),
  batch_number: z.string().optional().nullable().or(z.literal("")),
  expiry_date: z.date().optional().nullable(),
  dose_number: z.number().int().positive(t("positiveInteger")).default(1),
  has_next_dose: z.boolean().optional().default(false),
  next_dose_date: z.date().optional().nullable(),
  adverse_reaction: z.string().optional().nullable().or(z.literal("")),
  notes: z.string().optional().nullable().or(z.literal("")),
  next_applications: z.array(
    z.object({
      _key: z.string(),
      item_id: z.string().uuid().nullable(),
      item_label: z.string(),
      interval_days: z.number().nullable(),
      due_date: z.date().nullable(),
    })
  ).optional().default([]),
}).refine(
  (data) => data.item_id,
  {
    message: t("vaccineRequired"),
    path: ["item_id"],
  }
).superRefine((data, ctx) => {
  if (!data.has_next_dose) return;

  if (!data.next_application_item_id) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["next_application_item_id"],
      message: "vaccines.validation.nextApplicationRequired",
    });
  }

  if (!data.next_dose_date) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["next_dose_date"],
      message: "vaccines.validation.nextDoseDateRequiredWhenEnabled",
    });
  }
});

// Schema para protocolo de vacinação
export const vaccineProtocolSchema = z.object({
  name: z.string().min(1, t("nameRequired")).max(200, t("nameTooLong")),
  species_id: z.string().uuid().optional().or(z.literal("")),
  min_age_days: z.number().int().nonnegative(t("minAgeNonNegative")).optional(),
  max_age_days: z.number().int().positive(t("positiveInteger")).optional(),
  description: z.string().optional().or(z.literal("")),
  is_active: z.boolean().optional().default(true),
});

// Schema para item do protocolo
export const vaccineProtocolItemSchema = z.object({
  protocol_id: z.string().uuid(),
  vaccine_id: z.string().uuid(t("vaccineRequired")),
  sequence_order: z.number().int().positive(t("positiveInteger")),
  days_from_start: z.number().int().nonnegative(t("nonNegativeNumber")),
});

export type VaccineInput = z.infer<typeof vaccineSchema>;
export type PetVaccinationInput = z.infer<typeof petVaccinationSchema>;
export type VaccineProtocolInput = z.infer<typeof vaccineProtocolSchema>;
export type VaccineProtocolItemInput = z.infer<typeof vaccineProtocolItemSchema>;
